import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Comment } from 'services/comments/types';
import type { Event } from 'services/events/types';
import { getApiBaseUrl } from 'utils/user';
import { Objective, ObjectiveEntry } from './types';

export const apiObjectives = createApi({
  reducerPath: 'objectivesApi',
  tagTypes: ['Objective', 'Comment', 'Entry', 'Event'],
  baseQuery: fetchBaseQuery({
    baseUrl: `${getApiBaseUrl()}/`,
    credentials: 'include',
    prepareHeaders: (headers) => {
      headers.set('X-Client', 'web');
      return headers;
    },
  }),
  endpoints: builder => ({
    getObjectives: builder.query<Objective[], void>({
      query: () => 'objectives',
      transformResponse: (data: { objectives: Objective[] }) => data.objectives,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      providesTags: ['Objective'],
    }),
    getObjective: builder.query<Objective, string>({
      query: id => `objectives/${id}`,
      transformResponse: (data: { objective: Objective }) => data.objective,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      providesTags: ['Objective'],
    }),
    getObjectiveComments: builder.query<Comment[], string>({
      query: id => `objectives/${id}/comments`,
      transformResponse: (data: { comments: Comment[] }) => data.comments,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      providesTags: (_result, _error, id) => [{ type: 'Comment', id: `objective-${id}` }],
    }),
    addObjectiveComment: builder.mutation<Comment, { comment: string; objectiveId: string }>({
      query: ({ comment, objectiveId }) => ({
        url: `objectives/${objectiveId}/comments`,
        method: 'POST',
        body: { comment },
      }),
      transformResponse: (data: { comment: Comment }) => data.comment,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { objectiveId }) => [{ type: 'Comment', id: `objective-${objectiveId}` }],
    }),
    patchObjectiveComment: builder.mutation<Comment, { comment: string; id: string; objectiveId: string }>({
      query: ({ comment, id, objectiveId }) => ({
        url: `objectives/${objectiveId}/comments/${id}`,
        method: 'PATCH',
        body: { comment },
      }),
      transformResponse: (data: { comment: Comment }) => data.comment,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { objectiveId }) => [{ type: 'Comment', id: `objective-${objectiveId}` }],
    }),
    deleteObjectiveComment: builder.mutation<string, { id: string; objectiveId: string }>({
      query: ({ id, objectiveId }) => ({
        url: `objectives/${objectiveId}/comments/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (data: { commentId: string }) => data.commentId,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { objectiveId }) => [{ type: 'Comment', id: `objective-${objectiveId}` }],
    }),
    getObjectiveEntries: builder.query<ObjectiveEntry[], string>({
      query: id => `objectives/${id}/entries`,
      transformResponse: (data: { entries: ObjectiveEntry[] }) => data.entries,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      providesTags: (_result, _error, id) => [{ type: 'Entry', id }],
    }),
    addObjectiveEntry: builder.mutation<ObjectiveEntry, {
      note?: string;
      objectiveId: string;
      recordedAt: string;
      value: number;
    }>({
      query: ({ objectiveId, ...body }) => ({
        url: `objectives/${objectiveId}/entries`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: { entry: ObjectiveEntry }) => data.entry,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { objectiveId }) => [
        'Objective',
        { type: 'Entry', id: objectiveId },
        { type: 'Event', id: objectiveId },
      ],
    }),
    patchObjectiveEntry: builder.mutation<ObjectiveEntry, {
      id: string;
      note?: string;
      objectiveId: string;
      recordedAt: string;
      value: number;
    }>({
      query: ({ id, objectiveId, ...body }) => ({
        url: `objectives/${objectiveId}/entries/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (data: { entry: ObjectiveEntry }) => data.entry,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { objectiveId }) => [
        'Objective',
        { type: 'Entry', id: objectiveId },
        { type: 'Event', id: objectiveId },
      ],
    }),
    deleteObjectiveEntry: builder.mutation<string, { id: string; objectiveId: string }>({
      query: ({ id, objectiveId }) => ({
        url: `objectives/${objectiveId}/entries/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (data: { entryId: string }) => data.entryId,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { objectiveId }) => [
        'Objective',
        { type: 'Entry', id: objectiveId },
        { type: 'Event', id: objectiveId },
      ],
    }),
    getObjectiveEvents: builder.query<Event[], string>({
      query: id => `objectives/${id}/events`,
      transformResponse: (data: { events: Event[] }) => data.events,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      providesTags: (_result, _error, id) => [{ type: 'Event', id }],
    }),
    addObjective: builder.mutation<Objective, Partial<Objective>>({
      query: body => ({
        url: `objectives`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: { objective: Objective }) => data.objective,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: ['Objective'],
    }),
    patchObjective: builder.mutation<Objective, Partial<Objective> & Pick<Objective, 'id'>>({
      query: ({ id, ...body }) => ({
        url: `objectives/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (data: { objective: Objective }) => data.objective,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { id }) => ['Objective', { type: 'Event', id }],
    }),
    updateObjective: builder.mutation<Objective, Partial<Objective> & Pick<Objective, 'id'>>({
      query: ({ id, ...body }) => ({
        url: `objectives/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (data: { objective: Objective }) => data.objective,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { id }) => ['Objective', { type: 'Event', id }],
    }),
    deleteObjective: builder.mutation<string, string>({
      query: id => ({
        url: `objectives/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (data: { objectiveId: string }) => data.objectiveId,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: ['Objective'],
    }),
  }),
});

export const {
  useGetObjectivesQuery,
  useLazyGetObjectiveQuery,
  useGetObjectiveCommentsQuery,
  useGetObjectiveEntriesQuery,
  useGetObjectiveEventsQuery,
  useAddObjectiveEntryMutation,
  usePatchObjectiveEntryMutation,
  useDeleteObjectiveEntryMutation,
  useAddObjectiveCommentMutation,
  usePatchObjectiveCommentMutation,
  useDeleteObjectiveCommentMutation,
  useAddObjectiveMutation,
  usePatchObjectiveMutation,
  useUpdateObjectiveMutation,
  useDeleteObjectiveMutation,
} = apiObjectives;
